import {
  forgottenPasswordSchema,
  type ForgottenPasswordDTO,
} from '@kanbu/schema/contracts';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import { Input, type TypedFormState } from '@utima/ui-informed';

import { BasicForm } from '@/components/form/BasicForm';
import { trpc } from '@/services/trpc';

export const Route = createFileRoute('/_authLayout/forgotten-password/')({
  component: ForgottenPasswordPage,
});

const initialValues: ForgottenPasswordDTO = {
  email: '',
};

export function ForgottenPasswordPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const forgottenPasswordMutation = trpc.auth.forgottenPassword.useMutation();

  const onSubmit = (formState: TypedFormState<ForgottenPasswordDTO>) => {
    forgottenPasswordMutation.mutate(formState.values, {
      onError: (error: any) => {
        console.error(error);
        if (
          typeof error === 'object' &&
          error &&
          'data' in error &&
          typeof error.data === 'object' &&
          error.data &&
          'code' in error.data &&
          error.data.code === 'UNAUTHORIZED'
        ) {
          toast.error(t`Unverified email`, {
            description: t`Please verify your email address before resetting your password.`,
          });
        } else {
          toast.error(t`An error occurred`, {
            description: t`Could not send email, please try again.`,
          });
        }
      },
      onSuccess: () => {
        navigate({
          to: '/forgotten-password/sent',
          search: { email: formState.values.email },
        });
      },
    });
  };

  return (
    <BasicForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      zodSchema={forgottenPasswordSchema}
      className='flex w-full flex-col gap-6'
    >
      <h1 className='text-center text-2xl font-bold'>{t`Forgot password?`}</h1>

      <div className='text-center text-sm'>
        {t`Please enter the email address you used to create your account, and we'll send you a link to reset your password.`}
      </div>

      <Input
        name='email'
        type='email'
        data-cta='admin-forgotten-password-email-input'
        label={t`Email`}
        placeholder={t`Email`}
        required
      />

      <div className='flex justify-between gap-4'>
        <Button
          data-cta='admin-forgotten-password-back-to-login-button'
          variant='muted'
          outline
          asChild
        >
          <Link to='/login'>{t`Back to login`}</Link>
        </Button>
        <Button
          data-cta='admin-forgotten-password-send-button'
          variant='primary'
          type='submit'
        >
          {t`Send`}
        </Button>
      </div>
    </BasicForm>
  );
}
